import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Box, Breadcrumbs, Button, Typography, Card, CardContent } from '@material-ui/core'
import { useHistory } from 'react-router'
import { useTranslation } from 'react-i18next'
import Page from '../../../components/page/page'
import PageCenterColumn from '../../../components/page/pageCenterColumn'
import PageHeader from '../../../components/page/pageHeader'
import { BreadcrumbLink } from '../../../components/shared/breadcrumbLink/breadcrumbLink'
import { NonAuthRoutes } from '../../../model'
import { FormDialogDefinitionProps, useConfirmationDialog, useFormDialog } from '../../../hooks'
import {
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataType,
  FilterType,
  FormDialogMode,
  useNotify,
  UserPermission
} from '@globaleris/ui.library'
import { getCustomerDomain } from '../../../redux'
import { Delete, Edit } from '@material-ui/icons'
import {
  NewSettlementGroupSubjectModel,
  SettlementGroupSubjectModel
} from '../../../api/Argentia/endpoints/settlementGroups/model/settlementGroups'
import moment from 'moment'
import { EnumTypeSettlementGroup } from '../../../api/Argentia/enums/typeSettlementGroup'
import { CardEmptyContent } from '../../../components/shared/cardEmptyContent/cardEmptyContent'
import { Pages } from '../../../model/RBAC/constants'
import { getUserPermissions } from '../../../helpers/permissionsHelper'
import { fetchSubjects } from '../../../services/subjects.service'
import {
  createSettlementGroupSubjectList,
  deleteSettlementGroupSubject,
  getSettlementGroupsBusinessGroup,
  patchSettlementGroupSubjectList
} from '../settlementGroups.service'
import { ConfirmationDialogs } from '../../../model/enums/confirmationDialogs'
import { LoadingCard } from '../../../components/shared/loadingCard/loadingCard'

type SettlementGroupSubjectsPageProps = {
  location: {
    state: {
      settlementGroupId: number
      settlementGroupCode: string
      subjectList?: SettlementGroupSubjectModel[]
      flag: EnumTypeSettlementGroup
    }
  }
}

export const SettlementGroupSubjectsPage: React.FC<SettlementGroupSubjectsPageProps> = props => {
  const [t] = useTranslation(['globalerisLibrary', 'settlementGroups', 'enums'])
  const history = useHistory()

  const groupId = props.location.state?.settlementGroupId
  const groupCode = props.location.state?.settlementGroupCode
  const flag = props.location.state?.flag
  const subjectList = props.location.state?.subjectList ?? []

  const { info, error } = useNotify()
  const { getConfirmation, closeConfirmation } = useConfirmationDialog()
  const { openForm, closeForm, updateFormData, updateFormDefinition, formProps, CustomFormDialog } =
    useFormDialog()

  const [isFetching, setIsFetching] = useState<boolean>(false)
  const [subjectListState, setSubjectListState] =
    useState<SettlementGroupSubjectModel[]>(subjectList)
  const [subjectsSelectOptions, setSubjectsSelectOptions] = useState<any[]>([])

  const updateFormDataRef = useRef(updateFormData)
  updateFormDataRef.current = updateFormData

  const handleClickBackButton = () => {
    history.goBack()
  }

  const refetchSubjects = async () => {
    setIsFetching(true)
    try {
      const response = await getSettlementGroupsBusinessGroup()
      if (response.success) {
        const groups: any[] = response.data ?? []
        const currentGroup = groups.find(g => g.id === groupId)
        const updated = (currentGroup?.subjectList ?? []) as SettlementGroupSubjectModel[]
        setSubjectListState(updated)
      }
    } finally {
      setIsFetching(false)
    }
  }
  const validateDates = (formData, formType: string): boolean => {
    const endDateBeforeStartDate = formData.endDate
      ? moment(formData.endDate).isBefore(formData.startDate, 'day')
      : null

    if (endDateBeforeStartDate && (formType === 'ADD' || formType === 'EDIT')) {
      error(t('settlementGroups:components.subjects.forms.edit.validations.beforeEndDate'))
      return false
    } else {
      return true
    }
  }

  const handleAddDialogOpen = async () => {
    setSubjectsSelectOptions([])

    openForm({
      definition: formAddDefinition,
      title: t('settlementGroups:components.subjects.forms.add.title'),
      mode: FormDialogMode.ADD,
      isWaiting: false,
      inputData: undefined,
      onClose: onCloseAddHandler
    })
    return true
  }

  const onCloseAddHandler = async formData => {
    if (formData) {
      if (validateDates(formData, 'ADD')) {
        // Map data from CustomFormDialog (Modal)
        const request: NewSettlementGroupSubjectModel[] = formData.subjects.map(subjectId => {
          return {
            subjectId: subjectId,
            startDate: formData.startDate,
            endDate: formData.endDate ?? undefined
          } as NewSettlementGroupSubjectModel
        })

        await createSettlementGroupSubjectList(groupId, request).then(response => {
          if (response.success) {
            info(t('settlementGroups:components.subjects.forms.add.notifications.save'))
            refetchSubjects()
          } else {
            error(t('settlementGroups:components.subjects.forms.add.notifications.error'))
          }
        })
      } else {
        return
      }
    }
    closeForm()
  }

  const handleEditDialogOpen = async selectedRow => {
    openForm({
      definition: formEditDefinition,
      title: t('settlementGroups:components.subjects.forms.edit.title'),
      mode: FormDialogMode.EDIT,
      isWaiting: false,
      inputData: {
        ...selectedRow
      },
      onClose: onCloseEditHandler
    })

    return true
  }

  const onCloseEditHandler = async formData => {
    if (formData) {
      if (validateDates(formData, 'EDIT')) {
        const request: any = {
          startDate: formData.startDate,
          endDate: formData.endDate ?? undefined
        }

        await patchSettlementGroupSubjectList(groupId, formData.id, request).then(response => {
          if (response.success) {
            info(t('settlementGroups:components.subjects.forms.edit.notifications.save'))
            refetchSubjects()
          } else {
            error(t('settlementGroups:components.subjects.forms.edit.notifications.error'))
          }
        })
      } else {
        return
      }
    }
    closeForm()
  }

  const handleDeleteDialogOpen = selectedRow => {
    getConfirmation(ConfirmationDialogs.DELETE_SETTLEMENTGROUP_SUBJECT).then(async () => {
      await deleteSettlementGroupSubject(groupId, selectedRow.id).then(response => {
        if (response.success) {
          info(t('settlementGroups:components.subjects.notifications.delete.success'))
          refetchSubjects()
        } else {
          error(t('settlementGroups:components.subjects.notifications.delete.error'))
        }
      })
      closeConfirmation()
    })
  }

  // Form Definition Starts
  const formAddDefinition = React.useMemo<FormDialogDefinitionProps>(() => {
    const toSubjectOption = (x: any) => ({
      value: x.value,
      label: x.label
    })

    return {
      id: 'ADD',
      sections: [
        {
          inputs: [
            {
              name: 'subject',
              label: t('settlementGroups:components.subjects.forms.add.inputs.subject'),
              type: DataType.SEARCHFIELD,
              placeholder: t('settlementGroups:forms.add.placeholders.subject'),
              clearable: true,
              apiCall: async (query: string) => {
                const raw = await fetchSubjects(query)
                return (raw ?? []).map(toSubjectOption)
              },
              onChange: (selectedItem: any, formState: any) => {
                const itemValue = Number(selectedItem.value)

                setSubjectsSelectOptions(prev => {
                  const exists = prev.some(o => o.value === selectedItem.value)
                  return exists ? prev : [...prev, selectedItem]
                })

                const currentSubjects = Array.isArray(formState?.subjects) ? formState.subjects : []

                if (!currentSubjects.includes(itemValue)) {
                  const updatedFormState = {
                    ...formState,
                    subject: null,
                    subjects: [...currentSubjects, itemValue]
                  }

                  updateFormDataRef.current(updatedFormState)
                  updateFormData(updatedFormState)
                }
              }
            },
            {
              name: 'subjects',
              label: t('settlementGroups:components.subjects.forms.add.inputs.subjects'),
              type: DataType.MULTISELECT,
              selectOptions: subjectsSelectOptions,
              required: true,
              onChange: (item: any) => {
                setSubjectsSelectOptions(prev => (prev ?? []).filter(o => item.includes(o?.value)))
              }
            },
            {
              name: 'startDate',
              label: t('settlementGroups:components.subjects.forms.add.inputs.startDate'),
              type: DataType.DATE,
              required: true
            },
            {
              name: 'endDate',
              label: t('settlementGroups:components.subjects.forms.add.inputs.endDate'),
              type: DataType.DATE,
              clearable: true
            }
          ]
        }
      ]
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, subjectsSelectOptions])

  const formEditDefinition =
    React.useMemo<FormDialogDefinitionProps>((): FormDialogDefinitionProps => {
      return {
        id: 'EDIT',
        sections: [
          {
            inputs: [
              {
                name: 'deSujeto',
                label: t('settlementGroups:components.subjects.forms.edit.inputs.subject'),
                type: DataType.TEXT,
                readOnly: true
              },
              {
                name: 'startDate',
                label: t('settlementGroups:components.subjects.forms.edit.inputs.startDate'),
                type: DataType.DATE,
                required: true
              },
              {
                name: 'endDate',
                label: t('settlementGroups:components.subjects.forms.edit.inputs.endDate'),
                type: DataType.DATE,
                clearable: true
              }
            ]
          }
        ]
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

  // Form Definition Ends

  // DataTable Config Starts
  const dialogTableColumns: DataTableColumn[] = [
    {
      name: 'id',
      type: DataType.NUMBER,
      display: false
    },
    {
      name: 'deSujeto',
      label: t('settlementGroups:components.subjects.dataTable.columns.deSujeto'),
      type: DataType.TEXT
    },
    {
      name: 'documentoFiscal',
      label: t('settlementGroups:components.subjects.dataTable.columns.documentoFiscal'),
      type: DataType.TEXT
    },
    {
      name: 'startDate',
      label: t('settlementGroups:components.subjects.dataTable.columns.startDate'),
      type: DataType.DATE
    },
    {
      name: 'endDate',
      label: t('settlementGroups:components.subjects.dataTable.columns.endDate'),
      type: DataType.DATE
    }
  ]

  let dialogTableOptions: DataTableOptions = {
    i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
    analytics: {
      customer: getCustomerDomain(),
      view: t('settlementGroups:components.contracts.dataTable.analytics')
    },
    elevation: 0,
    filterType: FilterType.NONE,
    download: false,
    canEdit: false,
    canDelete: false,
    customInsertEditModal: true,
    pagination: true,
    rowsPerPage: 10,
    rowsPerPageOptions: undefined,
    onRowInsert: handleAddDialogOpen,
    rowCustomActions: [
      {
        icon: <Edit fontSize="small" />,
        tooltip: t('settlementGroups:components.subjects.dataTable.rowCustomActions.edit'),
        onClick: handleEditDialogOpen,
        userPermission: UserPermission.EDIT
      },
      {
        icon: <Delete fontSize="small" />,
        tooltip: t('settlementGroups:components.subjects.dataTable.rowCustomActions.delete'),
        onClick: handleDeleteDialogOpen,
        userPermission: UserPermission.EDIT
      }
    ]
  }

  // DataTable Config Ends

  const tableData = useMemo(() => {
    return (subjectListState ?? []).map((subject: any) => ({
      id: subject.id,
      deSujeto: subject.deSujeto,
      documentoFiscal: subject.documentoFiscal,
      startDate: subject.startDate ? moment(subject.startDate).toDate() : null,
      endDate: subject.endDate ? moment(subject.endDate).toDate() : null
    }))
  }, [subjectListState])

  // useEffect for updating Modal Form (EditForm) (customHook needed)
  useEffect(() => {
    updateFormDefinition(formAddDefinition)
    updateFormDefinition(formEditDefinition)
  }, [formAddDefinition, formEditDefinition])

  if (!groupCode || !flag) {
    history.push(NonAuthRoutes.SettlementGroups)
  }

  return (
    <Page fullWidth={true}>
      <PageHeader title={t('settlementGroups:components.subjects.title', { groupCode })}>
        <Button variant="text" color="primary" onClick={handleClickBackButton}>
          {t('settlementGroups:actions.back')}
        </Button>
      </PageHeader>
      <PageCenterColumn>
        <Box mb={1}>
          <Breadcrumbs separator=">" aria-label="breadcrumb">
            <BreadcrumbLink toPathName="/settlementGroups">
              {`${t('settlementGroups:breadcrumbs.settlementGroups')}`}
            </BreadcrumbLink>
            <Typography key="3" color="primary" variant="subtitle2">
              {t('settlementGroups:breadcrumbs.group', { groupCode })}
            </Typography>
            <Typography key="3" color="primary" variant="subtitle1">
              {t('settlementGroups:breadcrumbs.subjects')}
            </Typography>
          </Breadcrumbs>
        </Box>
        {isFetching ? (
          <LoadingCard />
        ) : subjectListState.length === 0 ? (
          <CardEmptyContent
            title={t('settlementGroups:components.subjects.emptyCard.title')}
            caption={t('settlementGroups:components.subjects.emptyCard.caption')}
          >
            <Button size="small" color="primary" variant="contained" onClick={handleAddDialogOpen}>
              {t('settlementGroups:components.subjects.emptyCard.buttonLabel')}
            </Button>
          </CardEmptyContent>
        ) : (
          <Card raised>
            <CardContent>
              <DataTable
                title={t('settlementGroups:components.subjects.dataTable.title')}
                data={tableData}
                columns={dialogTableColumns}
                options={dialogTableOptions}
                userPermissions={getUserPermissions(Pages.SettlementGroupAggregatedAdd)}
              />
            </CardContent>
          </Card>
        )}
        <CustomFormDialog {...formProps} />
      </PageCenterColumn>
    </Page>
  )
}
